import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Alert,
  Snackbar,
  Fab,
  CircularProgress,
} from '@mui/material';
import { AppIconButton, AppListPage } from './design-system';
import { useAppToolbar } from '../contexts/AppToolbarContext';
import {
  Add as AddIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  PlayArrow as PlayIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { providerTenantsService, type Tenant } from '../services/ProviderTenantsService';
import { type ProviderStatsResponseData } from '../../../shared/src/schemas.ts';
import TenantDetails from './TenantDetails';
import CreateTenantDialog from './dialogs/CreateTenantDialog';

const ProviderDashboard: React.FC = () => {
  const [stats, setStats] = useState<ProviderStatsResponseData | null>(null);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info' as 'success' | 'error' | 'warning' | 'info',
  });
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [showTenantDetails, setShowTenantDetails] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [statsData, tenantsData] = await Promise.all([
        providerTenantsService.getProviderStats(),
        providerTenantsService.getTenants(),
      ]);
      setStats(statsData);
      setTenants(tenantsData);
    } catch (error) {
      showSnackbar('Failed to load data: ' + (error instanceof Error ? error.message : 'Unknown error'), 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseTenantDetails = () => {
    setShowTenantDetails(false);
    setSelectedTenant(null);
  };

  const handleTenantUpdated = (updatedTenant: Tenant) => {
    setTenants(tenants.map(t => t.id === updatedTenant.id ? updatedTenant : t));
    setSnackbar({
      open: true,
      message: 'Tenant updated successfully',
      severity: 'success',
    });
  };

  const toolbarPageActions = useMemo(
    () => (
      <>
        <AppIconButton onClick={loadData} title="Refresh">
          <RefreshIcon />
        </AppIconButton>
        <Fab color="primary" size="medium" onClick={() => setCreateDialogOpen(true)} sx={{ ml: 1 }}>
          <AddIcon />
        </Fab>
      </>
    ),
    [loadData],
  );

  useAppToolbar({ pageActions: toolbarPageActions });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <AppListPage>
      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <BusinessIcon color="primary" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h4" component="div">
                    {stats?.totalTenants || 0}
                  </Typography>
                  <Typography color="text.secondary">
                    Total Tenants
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <PlayIcon color="success" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h4" component="div">
                    {stats?.activeTenants || 0}
                  </Typography>
                  <Typography color="text.secondary">
                    Active Tenants
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <PeopleIcon color="info" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h4" component="div">
                    {stats?.totalUsers || 0}
                  </Typography>
                  <Typography color="text.secondary">
                    Total Users
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TrendingUpIcon color="warning" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h4" component="div">
                    {0}
                  </Typography>
                  <Typography color="text.secondary">
                    New This Month
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Create Tenant Dialog */}
      <CreateTenantDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={(message) => showSnackbar(message, 'success')}
        onError={(message) => showSnackbar(message, 'error')}
        onTenantCreated={loadData}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Tenant Details Dialog */}
      <TenantDetails
        open={showTenantDetails}
        tenant={selectedTenant}
        onClose={handleCloseTenantDetails}
        onTenantUpdated={handleTenantUpdated}
      />
    </AppListPage>
  );
};

export default ProviderDashboard;
